import { Subtract } from "utility-types";
import * as React from "react";
import { Query } from "react-apollo";
import hoistNonReactStatics from "hoist-non-react-statics";

import { MeQuery, meQuery } from "../graphql/user";
import { clearAuthResponse } from "./client";

export type SessionProps = MeQuery & {
  sessionQuery: {
    loading: boolean;
    error: any | null;
  };
};

const sessionContext = React.createContext({
  me: undefined,
  sessionQuery: { loading: false, error: undefined },
} as SessionProps);

type Props = {
  children: React.ReactNode;
};

const SessionProvider = ({ children }: Props) => (
  <Query<MeQuery> query={meQuery}>
    {({ data, loading, error }) => {
      if (error) {
        const response =
          error.networkError && (error.networkError as any).response;
        if (response && response.status === 401) {
          // We can get this if our auth token has expired. We will clear the
          // auth response which will automatically trigger a new query.
          clearAuthResponse();
        }
      }
      return (
        <sessionContext.Provider
          value={{ ...data, sessionQuery: { loading, error } }}
          children={React.Children.only(children)}
        />
      );
    }}
  </Query>
);

function withSession<P extends SessionProps>(
  Component: React.ComponentType<P>
) {
  class WrapperComponent extends React.Component<Subtract<P, SessionProps>> {
    static contextType = sessionContext;

    render() {
      return <Component {...this.props} {...this.context} />;
    }
  }
  hoistNonReactStatics<any, any>(WrapperComponent, Component);
  return WrapperComponent;
}

export { SessionProvider, withSession };
